// src/components/Design/LeftSidebar.tsx
import { ActionIcon, Box, Tooltip } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setActiveLeftSideBarTabInRedux } from "../designSlice";
import { FaCheckDouble, FaInfo, FaQuestion } from "react-icons/fa6";
import { FaCog, FaProjectDiagram, FaUndo } from "react-icons/fa";
import { DesignSettings } from "./DesignSettings";
import { SpaceValidationTablePanel } from "./SpaceValidationTablePanel";
import HelpPanel from "./HelpPanel";
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { LEFT_SIDEBAR_TABS_BY_MODULE } from "@multiconsult-group/multiplacer-core";
import { DeviceHistoryPanel } from "../history/DeviceHistoryPanel";
import { McBimItemDetails } from "../../components/McBimItem/McBimItemDetails";
import { RoutingGraphsPanel } from "./RoutingGraphsPanel";

export const LeftSidebar: React.FC = () => {
  const activeTab = useAppSelector((state) => state.design.activeLeftSideBarTab);
  const spaceValidationRun = useAppSelector((state) => state.design.spaceValidationRun);
  const activeModule = useAppSelector((s) => s.design.activeModule); // "CEILING" | "RFP" | undefined

  // Om ingen modul er aktiv, vis ingenting i venstre sidepanel
  if (!activeModule) {
    return null;
  }

  return (
    <Box
      c="black"
      bg="#e1e1e1b8"
      h="100%"
      w="100%"
      style={{
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(8px)",
        minHeight: 0,
      }}
    >
      <Box
        h="100%"
        w="100%"
        style={{
          display: "flex",
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box style={{ zIndex: 2 }}>
          <IconVerticalBar />
        </Box>

        {activeTab != null && (
          <Box
            p="md"
            w="100%"
            h="100%"
            style={{
              flex: 1,
              minHeight: 0,
              maxHeight: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {activeTab === DesignLeftSideBarTabs.CEILING_HELP && <HelpPanel />}

            {activeTab === DesignLeftSideBarTabs.CEILING_VALIDATIONS &&
              spaceValidationRun.active === true && <SpaceValidationTablePanel />}

            {activeTab === DesignLeftSideBarTabs.CEILING_SETTINGS && <DesignSettings />}
            {activeTab === DesignLeftSideBarTabs.DEVICE_HISTORY && <DeviceHistoryPanel />}
            {activeTab === DesignLeftSideBarTabs.MCBIMITEM_INFO && <McBimItemDetails />}
            {activeTab === DesignLeftSideBarTabs.ROUTING_GRAPHS && <RoutingGraphsPanel />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const IconVerticalBar: React.FC = () => {
  const activeTab = useAppSelector((state) => state.design.activeLeftSideBarTab);
  const spaceValidationRun = useAppSelector((state) => state.design.spaceValidationRun);
  const activeModule = useAppSelector((s) => s.design.activeModule);
  const dispatch = useAppDispatch();

  // Hvilke tabs er i det hele tatt lov for denne modulen?
  const allowedTabs = (activeModule ? LEFT_SIDEBAR_TABS_BY_MODULE[activeModule] : undefined) ?? [];

  // Konfig per tab (label, ikon, tooltip)
  const allIconConfigs: {
    value: DesignLeftSideBarTabs;
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    tooltip: string;
    hide?: boolean;
  }[] = [
    {
      value: DesignLeftSideBarTabs.CEILING_VALIDATIONS,
      label: "Valideringer",
      icon: FaCheckDouble,
      hide: spaceValidationRun.active === false,
      tooltip: "Valideringer",
    },
    {
      value: DesignLeftSideBarTabs.CEILING_HELP,
      label: "Forklaringer",
      icon: FaQuestion,
      tooltip: "Forklaringer",
    },
    {
      value: DesignLeftSideBarTabs.CEILING_SETTINGS,
      label: "Innstillinger",
      icon: FaCog,
      tooltip: "Innstillinger",
    },
    {
      value: DesignLeftSideBarTabs.DEVICE_HISTORY,
      label: "Endringshistorikk",
      icon: FaUndo,
      tooltip: "Endringshistorikk",
    },
    {
      value: DesignLeftSideBarTabs.MCBIMITEM_INFO,
      label: "BIM-objektinfo",
      icon: FaInfo,
      tooltip: "BIM-objektinfo",
    },
    {
      value: DesignLeftSideBarTabs.ROUTING_GRAPHS,
      label: "Lagrede rør",
      icon: FaProjectDiagram,
      tooltip: "Lagrede rør",
    },
  ];

  const iconConfigs = allIconConfigs
    .filter((cfg) => allowedTabs.includes(cfg.value)) // filtrer på activeModule
    .filter((cfg) => !cfg.hide);

  const selectTab = (tab: DesignLeftSideBarTabs) => {
    if (activeTab === tab) {
      dispatch(setActiveLeftSideBarTabInRedux(undefined));
    } else {
      dispatch(setActiveLeftSideBarTabInRedux(tab));
    }
  };

  if (iconConfigs.length === 0) {
    return null;
  }

  return (
    <Box h="100%" bg="gray.2">
      <ActionIcon.Group orientation="vertical">
        {iconConfigs.map((iconItem) => {
          const Icon = iconItem.icon;
          const isActive = activeTab === iconItem.value;

          return (
            <Tooltip label={iconItem.tooltip} key={iconItem.value} position="right">
              <ActionIcon
                style={{ outline: "none" }}
                variant={isActive ? "filled" : "default"}
                size="md"
                aria-label={iconItem.label}
                color={isActive ? "blue" : "gray"}
                onClick={() => selectTab(iconItem.value)}
              >
                <Icon />
              </ActionIcon>
            </Tooltip>
          );
        })}
      </ActionIcon.Group>
    </Box>
  );
};
